/**
 * Tech Rider & TBL tab for Profile Event Essentials.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, List } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../../../components/ui/Button';
import { useTechRiderTblStore } from '../../../stores/techRiderTblStore';
import { Loader } from '@/components/ui';
import { cn } from '../../../utils/cn';

const TECH_TBL_MIN_CHARS = 100;
const TECH_TBL_MAX_CHARS = 1000;

export const TechRiderTblTab: React.FC = () => {
    const [techRiderSaveLoading, setTechRiderSaveLoading] = useState(false);
    const [tblSaveLoading, setTblSaveLoading] = useState(false);

    const techRiderTblData = useTechRiderTblStore((s) => s.data);
    const techRiderTblLoading = useTechRiderTblStore((s) => s.isLoading);
    const ensureTechRiderTblFetched = useTechRiderTblStore((s) => s.ensureFetched);
    const putTechRiderTbl = useTechRiderTblStore((s) => s.putTechRiderTbl);

    const [techRiderEnabled, setTechRiderEnabled] = useState(false);
    const [techRiderContent, setTechRiderContent] = useState('');
    const [techRiderContentError, setTechRiderContentError] = useState<string | null>(null);
    const [tblEnabled, setTblEnabled] = useState(false);
    const [tblContent, setTblContent] = useState('');
    const [tblContentError, setTblContentError] = useState<string | null>(null);

    useEffect(() => {
        ensureTechRiderTblFetched();
    }, [ensureTechRiderTblFetched]);

    useEffect(() => {
        if (techRiderTblData == null) return;
        setTechRiderEnabled(!!techRiderTblData.tech_rider);
        setTechRiderContent(techRiderTblData.tech_rider_desc ?? '');
        setTblEnabled(!!techRiderTblData.tbl);
        setTblContent(techRiderTblData.tbl_desc ?? '');
        setTechRiderContentError(null);
        setTblContentError(null);
    }, [techRiderTblData]);

    const handleTechRiderToggle = () => {
        const serverTechRiderEnabled = !!techRiderTblData?.tech_rider;

        // If GET API currently has tech_rider === false, only toggle UI state
        // and require user to save via the button to hit PUT.
        if (!serverTechRiderEnabled) {
            const nextEnabled = !techRiderEnabled;
            setTechRiderEnabled(nextEnabled);
            if (nextEnabled) {
                toast('Switching Tech Rider on requires additional details. Please fill in the Tech Rider information and click "Save Tech Rider".');
            }
            return;
        }

        // If GET API already has tech_rider === true, the toggle directly controls PUT.
        if (techRiderEnabled) {
            setTechRiderEnabled(false);
            putTechRiderTbl({ tech_rider: false }).catch(() => setTechRiderEnabled(true));
            return;
        }
        setTechRiderEnabled(true);
        putTechRiderTbl({ tech_rider: true, tech_rider_desc: techRiderContent.trim() || '' }).catch(() => setTechRiderEnabled(false));
    };

    const handleTblToggle = () => {
        const serverTblEnabled = !!techRiderTblData?.tbl;

        // If GET API currently has tbl === false, only toggle UI state
        // and require user to save via the button to hit PUT.
        if (!serverTblEnabled) {
            const nextEnabled = !tblEnabled;
            setTblEnabled(nextEnabled);
            if (nextEnabled) {
                toast('Switching TBL details on makes them mandatory. Please fill in the TBL information and click "Save TBL".');
            }
            return;
        }

        // If GET API already has tbl === true, the toggle directly controls PUT.
        if (tblEnabled) {
            setTblEnabled(false);
            putTechRiderTbl({ tbl: false }).catch(() => setTblEnabled(true));
            return;
        }
        setTblEnabled(true);
        putTechRiderTbl({ tbl: true, tbl_desc: tblContent.trim() || '' }).catch(() => setTblEnabled(false));
    };

    const handleSaveTechRider = async () => {
        if (!techRiderEnabled) return;
        const trimmed = techRiderContent.trim();
        if (!trimmed) {
            const msg = `Tech Rider is required (${TECH_TBL_MIN_CHARS}–${TECH_TBL_MAX_CHARS} characters).`;
            setTechRiderContentError(msg);
            toast.error(msg);
            return;
        }
        if (trimmed.length < TECH_TBL_MIN_CHARS) {
            const msg = `Tech Rider must be at least ${TECH_TBL_MIN_CHARS} characters (currently ${trimmed.length}).`;
            setTechRiderContentError(msg);
            toast.error(msg);
            return;
        }
        if (trimmed.length > TECH_TBL_MAX_CHARS) {
            const msg = `Tech Rider must be at most ${TECH_TBL_MAX_CHARS} characters (currently ${trimmed.length}).`;
            setTechRiderContentError(msg);
            toast.error(msg);
            return;
        }
        setTechRiderContentError(null);
        setTechRiderSaveLoading(true);
        try {
            await putTechRiderTbl({ tech_rider: true, tech_rider_desc: trimmed });
        } finally {
            setTechRiderSaveLoading(false);
        }
    };

    const handleSaveTbl = async () => {
        if (!tblEnabled) return;
        const trimmed = tblContent.trim();
        if (!trimmed) {
            const msg = `TBL is required (${TECH_TBL_MIN_CHARS}–${TECH_TBL_MAX_CHARS} characters).`;
            setTblContentError(msg);
            toast.error(msg);
            return;
        }
        if (trimmed.length < TECH_TBL_MIN_CHARS) {
            const msg = `TBL must be at least ${TECH_TBL_MIN_CHARS} characters (currently ${trimmed.length}).`;
            setTblContentError(msg);
            toast.error(msg);
            return;
        }
        if (trimmed.length > TECH_TBL_MAX_CHARS) {
            const msg = `Content must be at most ${TECH_TBL_MAX_CHARS} characters (currently ${trimmed.length}).`;
            setTblContentError(msg);
            toast.error(msg);
            return;
        }
        setTblContentError(null);
        setTblSaveLoading(true);
        try {
            await putTechRiderTbl({ tbl: true, tbl_desc: trimmed });
        } finally {
            setTblSaveLoading(false);
        }
    };

    return (
        <motion.div
            key="tech"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
        >
            {techRiderTblLoading ? (
                <div className="flex justify-center py-12">
                    <Loader className="h-8 w-8 text-[#F33363]" />
                </div>
            ) : (
                <>
                    <section className="rounded-2xl space-y-5">
                        <div className="flex flex-row sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                                    <Music className="h-5 w-5 text-[#F33363]" />
                                    Tech Rider
                                </h2>
                                <p className="text-sm text-[#8E8E93] mt-1 max-w-md">Technical requirements and rider details for your events.</p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={techRiderEnabled}
                                onClick={handleTechRiderToggle}
                                className={cn(
                                    'relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1C1E]',
                                    'h-7 w-12 flex items-center',
                                    techRiderEnabled ? 'bg-[#F33363]' : 'bg-[#3A3A3C]'
                                )}
                            >
                                <span className={cn('inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200', techRiderEnabled ? 'translate-x-5' : 'translate-x-0.5')} />
                            </button>
                        </div>
                        <AnimatePresence initial={false}>
                            {techRiderEnabled && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="space-y-4"
                                >
                                    <div>
                                        {/* <label className="mb-2 block text-sm font-medium text-white">Content (required when Tech Rider is on)</label> */}
                                        <textarea
                                            value={techRiderContent}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                const prevLen = techRiderContent.length;
                                                setTechRiderContent(next);
                                                if (techRiderContentError) setTechRiderContentError(null);
                                                if (next.length === TECH_TBL_MAX_CHARS && prevLen !== TECH_TBL_MAX_CHARS) {
                                                    toast(`Tech Rider reached the maximum of ${TECH_TBL_MAX_CHARS} characters.`);
                                                }
                                            }}
                                            onBlur={() => {
                                                if (!techRiderEnabled) return;
                                                const trimmed = techRiderContent.trim();
                                                if (!trimmed) return;
                                                if (trimmed.length < TECH_TBL_MIN_CHARS) {
                                                    const msg = `Tech Rider must be at least ${TECH_TBL_MIN_CHARS} characters (currently ${trimmed.length}).`;
                                                    setTechRiderContentError(msg);
                                                    toast.error(msg);
                                                }
                                            }}
                                            placeholder={`Type your tech rider details here (${TECH_TBL_MIN_CHARS}–${TECH_TBL_MAX_CHARS} characters)...`}
                                            rows={6}
                                            maxLength={TECH_TBL_MAX_CHARS}
                                            className={cn(
                                                'flex w-full rounded-xl bg-transparent border border-[#3A3A3C] px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] resize-y min-h-[120px]',
                                                techRiderContentError && 'ring-2 ring-red-500'
                                            )}
                                        />
                                        <div className="mt-1.5 flex items-end justify-end gap-2">
                                            <span className="text-xs text-[#8E8E93]">
                                                {techRiderContent.length} / {TECH_TBL_MAX_CHARS} characters (min {TECH_TBL_MIN_CHARS})
                                            </span>
                                            {/* {techRiderContentError && <span className="text-xs text-red-500">{techRiderContentError}</span>} */}
                                        </div>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        className="rounded-full py-2.5 px-5 min-h-[44px] bg-[#151517] border border-[#3A3A3C] text-[#F2F2F7] hover:bg-[#2C2C2E]"
                                        onClick={handleSaveTechRider}
                                        disabled={!techRiderContent.trim()}
                                        isLoading={techRiderSaveLoading}
                                    >
                                        Save Tech Rider
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </section>

                    <section className="rounded-2xl space-y-5">
                        <div className="flex flex-row sm:flex-row sm:items-center sm:justify-between gap-4 ">
                            <div>
                                <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                                    <List className="h-5 w-5 text-[#F33363]" />
                                    TBL
                                </h2>
                                <p className="text-sm text-[#8E8E93] mt-1 max-w-md">Table and backline requirements for your gigs.</p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={tblEnabled}
                                onClick={handleTblToggle}
                                className={cn(
                                    'relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C1C1E]',
                                    'h-7 w-12 flex items-center',
                                    tblEnabled ? 'bg-[#F33363]' : 'bg-[#3A3A3C]'
                                )}
                            >
                                <span className={cn('inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200', tblEnabled ? 'translate-x-5' : 'translate-x-0.5')} />
                            </button>
                        </div>
                        <AnimatePresence initial={false}>
                            {tblEnabled && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="space-y-4 "
                                >
                                    <div>
                                        {/* <label className="mb-2 block text-sm font-medium text-white">Content (required when TBL is on)</label> */}
                                        <textarea
                                            value={tblContent}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                const prevLen = tblContent.length;
                                                setTblContent(next);
                                                if (tblContentError) setTblContentError(null);
                                                if (next.length === TECH_TBL_MAX_CHARS && prevLen !== TECH_TBL_MAX_CHARS) {
                                                    toast(`TBL reached the maximum of ${TECH_TBL_MAX_CHARS} characters.`);
                                                }
                                            }}
                                            onBlur={() => {
                                                if (!tblEnabled) return;
                                                const trimmed = tblContent.trim();
                                                if (!trimmed) return;
                                                if (trimmed.length < TECH_TBL_MIN_CHARS) {
                                                    const msg = `TBL must be at least ${TECH_TBL_MIN_CHARS} characters (currently ${trimmed.length}).`;
                                                    setTblContentError(msg);
                                                    toast.error(msg);
                                                }
                                            }}
                                            placeholder={`Type your TBL details here (${TECH_TBL_MIN_CHARS}–${TECH_TBL_MAX_CHARS} characters)...`}
                                            rows={6}
                                            maxLength={TECH_TBL_MAX_CHARS}
                                            className={cn(
                                                'flex w-full rounded-xl bg-transparent border border-[#3A3A3C] px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] resize-y min-h-[120px]',
                                                tblContentError && 'ring-2 ring-red-500'
                                            )}
                                        />
                                        <div className="mt-1.5 flex items-end justify-end gap-2">
                                            <span className="text-xs text-[#8E8E93]">
                                                {tblContent.length} / {TECH_TBL_MAX_CHARS} characters (min {TECH_TBL_MIN_CHARS})
                                            </span>
                                            {/* {tblContentError && <span className="text-xs text-red-500">{tblContentError}</span>} */}
                                        </div>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        className="rounded-full py-2.5 px-5 min-h-[44px] bg-[#151517] border border-[#3A3A3C] text-[#F2F2F7] hover:bg-[#2C2C2E]"
                                        onClick={handleSaveTbl}
                                        disabled={!tblContent.trim()}
                                        isLoading={tblSaveLoading}
                                    >
                                        Save TBL
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </section>
                </>
            )}
        </motion.div>
    );
};
